import { useEffect } from "react";
import { useNavigate } from "react-router";
import { wordTypeLabel, type WordType } from "../../domain/vocabulary/types.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useGeneration } from "../hooks/bindings.ts";
import { continueLabel } from "../view-models/labels.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function ChooseMeaningsScreen() {
  const state = useGeneration();
  const { generation } = useAppServices();
  const navigate = useNavigate();

  useEffect(() => {
    if (state.phase === "idle" || (state.phase === "error" && !state.draft)) {
      navigate("/add", { replace: true });
    }
    if (state.phase === "review" || state.phase === "generating") {
      navigate("/add/review", { replace: true });
    }
    if (state.phase === "existing" && state.existingWordId) {
      navigate(`/words/${state.existingWordId}?from=add`, { replace: true });
    }
  }, [state.phase, state.draft, state.existingWordId, navigate]);

  const draft = state.draft;
  const grouped = new Map<WordType, NonNullable<typeof draft>["candidates"]>();
  for (const candidate of draft?.candidates ?? []) {
    const list = grouped.get(candidate.type) ?? [];
    list.push(candidate);
    grouped.set(candidate.type, list);
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Choose Meanings" backTo="/add" />
        {state.phase === "discovering" ? (
          <div className={styles.empty}>{state.progressMessage ?? "Finding useful meanings…"}</div>
        ) : null}
        {state.error && state.draft ? <p className={styles.error}>{state.error.message}</p> : null}
        {draft && (state.phase === "choose" || Boolean(draft.candidates.length)) ? (
          <>
            <h1 className={styles.heroWord}>{draft.displayText}</h1>
            <p className={styles.sub}>
              Which meanings do you want to learn?
              <br />
              Choose one or more.
            </p>
            {draft.input.context ? <div className={styles.banner}>“{draft.input.context}”</div> : null}
            {[...grouped.entries()].map(([type, candidates]) => (
              <section key={type}>
                <h2 className={styles.typeHeading}>{wordTypeLabel(type)}</h2>
                {candidates.map((candidate) => {
                  const saved = candidate.match.kind === "saved";
                  const selected = draft.selectedCandidateIds.includes(candidate.id);
                  return (
                    <label
                      key={candidate.id}
                      className={`${styles.sense} ${saved ? styles.senseSaved : ""} ${selected ? styles.senseSelected : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={saved || selected}
                        disabled={saved}
                        onChange={() => generation.toggleCandidate(candidate.id)}
                      />
                      <div>
                        <TypeTag type={candidate.type} />
                        {saved ? <span className={styles.savedLabel}>Already saved</span> : null}
                        <h3>{candidate.shortLabel}</h3>
                        <p>{candidate.example}</p>
                        {candidate.match.kind === "uncertain" ? (
                          <p className={styles.error}>
                            Possible overlap with a saved meaning. Open it or confirm this is distinct.
                          </p>
                        ) : null}
                        {candidate.contextMatch ? <p className={styles.practiceStatus}>Matches your sentence</p> : null}
                      </div>
                    </label>
                  );
                })}
              </section>
            ))}
            <div className={styles.textActionList}>
              {draft.moreAvailable ? (
                <button className={styles.textButton} type="button" onClick={() => void generation.showMore()}>
                  Show more meanings
                </button>
              ) : null}
              <button className={styles.textButton} type="button" onClick={() => navigate("/add")}>
                Can’t find your meaning? Add context
              </button>
            </div>
            <div className={styles.footerAction}>
              <PrimaryButton
                disabled={!draft.selectedCandidateIds.length || state.phase === "generating"}
                onClick={() => void generation.continueWithSelection()}
              >
                {continueLabel(draft.selectedCandidateIds.length)}
              </PrimaryButton>
              <p className={styles.sub} style={{ textAlign: "center", marginTop: 9 }}>
                One focused learning card per meaning.
              </p>
            </div>
          </>
        ) : null}
      </Screen>
    </Frame>
  );
}
